import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  alpha,
  Stack,
  IconButton,
  Fade,
  LinearProgress,
  Theme,
} from '@mui/material';
import { Lock, ArrowBack, Visibility, VisibilityOff, CheckCircle, ErrorOutline } from '@mui/icons-material';
import { api } from '@services';
import axios, { AxiosError } from 'axios';
import { Button } from '@shared/components/ui/Button';
import designSystem from '@shared/utils/designSystem';
import {
  getGlassmorphismBackground,
  getPageBackgroundGradient,
  getBackgroundPattern,
  getBackButtonStyles,
  getTextFieldStyles
} from '../styles/authStyles';

interface ApiErrorResponse {
  error?: string;
  message?: string;
}

function isAxiosErrorWithResponse(error: unknown): error is AxiosError<ApiErrorResponse> {
  return axios.isAxiosError(error) && error.response !== undefined;
}

// Password strength validation
const validatePassword = (password: string) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const passedChecks = Object.values(checks).filter(Boolean).length;
  const strength = (passedChecks / 5) * 100;

  return { checks, strength, isValid: passedChecks === 5 };
};

const getStrengthColor = (strength: number, theme: Theme) => {
  if (strength < 40) return theme.palette.error.main;
  if (strength < 80) return theme.palette.warning.main;
  return theme.palette.success.main;
};

const getStrengthLabel = (strength: number) => {
  if (strength < 40) return 'Weak';
  if (strength < 80) return 'Medium';
  return 'Strong';
};

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const token = searchParams.get('token') || '';

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const passwordValidation = validatePassword(password);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError('No reset token provided. Please request a new password reset.');
        setValidating(false);
        return;
      }

      try {
        await api.post('/auth/validate-reset-token', { token });
        setTokenValid(true);
      } catch (error: unknown) {
        if (isAxiosErrorWithResponse(error)) {
          setTokenError(error.response?.data?.error || 'This reset link is invalid or has expired.');
        } else {
          setTokenError('Failed to validate reset link. Please try again.');
        }
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');

    // Validate password match
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    // Validate password strength
    if (!passwordValidation.isValid) {
      setError('Password does not meet the requirements.');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', { token, password });
      setResetComplete(true);
    } catch (error: unknown) {
      if (isAxiosErrorWithResponse(error)) {
        setError(error.response?.data?.error || 'Failed to reset password. Please try again.');
      } else if (error instanceof Error && error.message === 'Network Error') {
        setError('Unable to connect to server. Please check your connection.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state - validating token
  if (validating) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: getPageBackgroundGradient(theme),
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={48} sx={{ color: theme.palette.primary.main }} />
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontFamily: designSystem.typography.fontFamily.base,
              fontWeight: designSystem.typography.fontWeight.medium,
            }}
          >
            Validating reset link...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Token invalid state
  if (!tokenValid && !validating) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: getPageBackgroundGradient(theme),
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: theme.palette.mode === 'light' ? 0.4 : 0.3,
            background: getBackgroundPattern(theme),
          }}
        />

        <IconButton
          onClick={() => navigate('/login')}
          sx={getBackButtonStyles(theme)}
        >
          <ArrowBack />
        </IconButton>

        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="sm">
            <Fade appear={false} in timeout={800}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: `${designSystem.borderRadius.md}px`,
                  ...getGlassmorphismBackground(theme),
                  boxShadow: designSystem.shadows.xl,
                  overflow: 'hidden',
                  mx: { xs: 2, sm: 4 },
                }}
              >
                <CardContent sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: alpha(theme.palette.error.main, 0.15),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                    }}
                  >
                    <ErrorOutline sx={{ fontSize: 40, color: theme.palette.error.main }} />
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: designSystem.typography.fontWeight.bold,
                      mb: 2,
                      color: theme.palette.text.primary,
                      fontFamily: designSystem.typography.fontFamily.base,
                    }}
                  >
                    Link expired
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 4,
                      fontFamily: designSystem.typography.fontFamily.base,
                      lineHeight: 1.6,
                    }}
                  >
                    {tokenError}
                  </Typography>

                  <Button
                    fullWidth
                    hierarchy="primary"
                    size="large"
                    onClick={() => navigate('/forgot-password')}
                    sx={{
                      py: designSystem.spacing.sm / 8 + 0.75,
                      fontSize: designSystem.typography.fontSize.base,
                      fontFamily: designSystem.typography.fontFamily.base,
                      mb: 2,
                    }}
                  >
                    Request new reset link
                  </Button>

                  <Button
                    fullWidth
                    hierarchy="tertiary"
                    onClick={() => navigate('/login')}
                    sx={{
                      fontFamily: designSystem.typography.fontFamily.base,
                    }}
                  >
                    Back to sign in
                  </Button>
                </CardContent>
              </Card>
            </Fade>
          </Container>
        </Box>
      </Box>
    );
  }

  // Success state - password reset complete
  if (resetComplete) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: getPageBackgroundGradient(theme),
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: theme.palette.mode === 'light' ? 0.4 : 0.3,
            background: getBackgroundPattern(theme),
          }}
        />

        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="sm">
            <Fade appear={false} in timeout={800}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: `${designSystem.borderRadius.md}px`,
                  ...getGlassmorphismBackground(theme),
                  boxShadow: designSystem.shadows.xl,
                  overflow: 'hidden',
                  mx: { xs: 2, sm: 4 },
                }}
              >
                <CardContent sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: alpha(theme.palette.success.main, 0.15),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 40, color: theme.palette.success.main }} />
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: designSystem.typography.fontWeight.bold,
                      mb: 2,
                      color: theme.palette.text.primary,
                      fontFamily: designSystem.typography.fontFamily.base,
                    }}
                  >
                    Password reset successful
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 4,
                      fontFamily: designSystem.typography.fontFamily.base,
                      lineHeight: 1.6,
                    }}
                  >
                    Your password has been updated. You can now sign in with your new password.
                  </Typography>

                  <Button
                    fullWidth
                    hierarchy="primary"
                    size="large"
                    onClick={() => navigate('/login')}
                    sx={{
                      py: designSystem.spacing.sm / 8 + 0.75,
                      fontSize: designSystem.typography.fontSize.base,
                      fontFamily: designSystem.typography.fontFamily.base,
                    }}
                  >
                    Sign in
                  </Button>
                </CardContent>
              </Card>
            </Fade>
          </Container>
        </Box>
      </Box>
    );
  }

  // Password reset form
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: getPageBackgroundGradient(theme),
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: theme.palette.mode === 'light' ? 0.4 : 0.3,
          background: getBackgroundPattern(theme),
        }}
      />

      <IconButton
        onClick={() => navigate('/login')}
        sx={getBackButtonStyles(theme)}
      >
        <ArrowBack />
      </IconButton>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Container maxWidth="sm">
          <Fade appear={false} in timeout={800}>
            <Card
              elevation={0}
              sx={{
                borderRadius: `${designSystem.borderRadius.md}px`,
                ...getGlassmorphismBackground(theme),
                boxShadow: designSystem.shadows.xl,
                overflow: 'hidden',
                mx: { xs: 2, sm: 4 },
              }}
            >
              <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: designSystem.typography.fontWeight.bold,
                    mb: designSystem.spacing.sm / 8,
                    color: theme.palette.text.primary,
                    textAlign: 'center',
                    fontSize: { xs: '1.75rem', md: '2.25rem' },
                    fontFamily: designSystem.typography.fontFamily.base,
                    letterSpacing: '-0.02em',
                  }}
                >
                  Create new password
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: designSystem.spacing.xl / 8,
                    textAlign: 'center',
                    fontSize: designSystem.typography.fontSize.base,
                    fontWeight: designSystem.typography.fontWeight.medium,
                    fontFamily: designSystem.typography.fontFamily.base,
                  }}
                >
                  Enter your new password below.
                </Typography>

                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: designSystem.spacing.lg / 8,
                      borderRadius: `${designSystem.borderRadius.sm}px`,
                      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                      backgroundColor: alpha(theme.palette.error.main, 0.15),
                      color: theme.palette.mode === 'light'
                        ? theme.palette.error.dark
                        : theme.palette.error.light,
                      '& .MuiAlert-message': {
                        fontWeight: designSystem.typography.fontWeight.medium,
                        fontFamily: designSystem.typography.fontFamily.base,
                      },
                      '& .MuiAlert-icon': {
                        color: theme.palette.mode === 'light'
                          ? theme.palette.error.dark
                          : theme.palette.error.light,
                      },
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: designSystem.spacing.xs / 8,
                          color: theme.palette.text.primary,
                          fontWeight: designSystem.typography.fontWeight.semibold,
                          fontSize: designSystem.typography.fontSize.sm,
                          fontFamily: designSystem.typography.fontFamily.base,
                        }}
                      >
                        New password
                      </Typography>
                      <TextField
                        fullWidth
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        autoComplete="new-password"
                        autoFocus
                        disabled={loading}
                        placeholder="Enter new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <Lock sx={{ color: theme.palette.text.secondary, mr: 1.5, ml: 0.5, fontSize: 20 }} />
                          ),
                          endAdornment: (
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                              sx={{
                                mr: 0.5,
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  color: theme.palette.primary.main,
                                },
                              }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          ),
                        }}
                        sx={getTextFieldStyles(theme)}
                      />

                      {/* Password strength indicator */}
                      {password && (
                        <Box sx={{ mt: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontFamily: designSystem.typography.fontFamily.base,
                              }}
                            >
                              Password strength
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: getStrengthColor(passwordValidation.strength, theme),
                                fontWeight: designSystem.typography.fontWeight.semibold,
                                fontFamily: designSystem.typography.fontFamily.base,
                              }}
                            >
                              {getStrengthLabel(passwordValidation.strength)}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={passwordValidation.strength}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: alpha(theme.palette.text.primary, 0.1),
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getStrengthColor(passwordValidation.strength, theme),
                                borderRadius: 2,
                              },
                            }}
                          />
                          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                            {[
                              { key: 'length', label: 'At least 8 characters' },
                              { key: 'uppercase', label: 'One uppercase letter' },
                              { key: 'lowercase', label: 'One lowercase letter' },
                              { key: 'number', label: 'One number' },
                              { key: 'special', label: 'One special character' },
                            ].map(({ key, label }) => (
                              <Typography
                                key={key}
                                variant="caption"
                                sx={{
                                  color: passwordValidation.checks[key as keyof typeof passwordValidation.checks]
                                    ? theme.palette.success.main
                                    : theme.palette.text.secondary,
                                  fontFamily: designSystem.typography.fontFamily.base,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {passwordValidation.checks[key as keyof typeof passwordValidation.checks] ? '✓' : '○'} {label}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: designSystem.spacing.xs / 8,
                          color: theme.palette.text.primary,
                          fontWeight: designSystem.typography.fontWeight.semibold,
                          fontSize: designSystem.typography.fontSize.sm,
                          fontFamily: designSystem.typography.fontFamily.base,
                        }}
                      >
                        Confirm new password
                      </Typography>
                      <TextField
                        fullWidth
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        autoComplete="new-password"
                        disabled={loading}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        error={confirmPassword.length > 0 && password !== confirmPassword}
                        helperText={confirmPassword.length > 0 && password !== confirmPassword ? 'Passwords do not match' : ''}
                        InputProps={{
                          startAdornment: (
                            <Lock sx={{ color: theme.palette.text.secondary, mr: 1.5, ml: 0.5, fontSize: 20 }} />
                          ),
                          endAdornment: (
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              size="small"
                              sx={{
                                mr: 0.5,
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  color: theme.palette.primary.main,
                                },
                              }}
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          ),
                        }}
                        sx={getTextFieldStyles(theme)}
                      />
                    </Box>

                    <Box sx={{ pt: 1 }}>
                      <Button
                        type="submit"
                        fullWidth
                        hierarchy="primary"
                        size="large"
                        disabled={loading || !passwordValidation.isValid || password !== confirmPassword}
                        sx={{
                          py: designSystem.spacing.sm / 8 + 0.75,
                          fontSize: designSystem.typography.fontSize.base,
                          fontFamily: designSystem.typography.fontFamily.base,
                        }}
                      >
                        {loading ? (
                          <Stack direction="row" spacing={2} alignItems="center">
                            <CircularProgress size={20} color="inherit" />
                            <Typography
                              sx={{
                                fontFamily: designSystem.typography.fontFamily.base,
                                fontWeight: designSystem.typography.fontWeight.semibold,
                              }}
                            >
                              Resetting...
                            </Typography>
                          </Stack>
                        ) : (
                          'Reset password'
                        )}
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default ResetPasswordPage;
